import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ApiRequestService} from 'src/app/shared/api-request.service';
import {MatTableDataSource} from "@angular/material/table";
import {UtilsService} from "../../../shared/utils.service";
import {ChartsUtilsService} from "../../charts-utils.service";
import {IncidentsFilter} from "../../../incidents/incidents-filter/incidents-filter.interface";

@Component({
  selector: 'app-incidents-stats-table',
  templateUrl: './incidents-stats-table.component.html',
  styleUrls: ['./incidents-stats-table.component.css']
})
export class IncidentsStatsTableComponent implements OnInit {

  // The filters to use for API requests.
  @Input('filters') filters: IncidentsFilter;

  // Input to switch near realtime data polling on or off. This can only be single or multiple.
  @Input('query_type') query_type: string;

  dataSource = new MatTableDataSource<IncidentStatistic>();
  displayedColumns: string[] = ['header', 'data'];

  // Used to pass a reference to this component back to the parent component.
  @Output() referenceEvent: EventEmitter<IncidentsStatsTableComponent> = new EventEmitter<IncidentsStatsTableComponent>();

  // Determine if the chart can be downloaded or not.
  @Input('canBeDownloaded') canBeDownloaded: boolean;

  // The name of the chart for the parent card title.
  cardTitle: string = 'Incidents Stats';

  constructor(
    private api: ApiRequestService,
    private utils: UtilsService,
    public chartsUtils: ChartsUtilsService
  ) { }

  ngOnInit(): void {
    // Validate the default config.
    this.validateDefaultConfig();
    // Get the data from the API.
    this.getData();
    // Send a reference to the parent component.
    this.referenceEvent.emit(this);
  }

  /**
   * Validates the configuration of a chart.
   * Ensures that certain properties are defined and sets default values if not provided.
   *
   * @return {void}
   */
  validateDefaultConfig(): void {
    // Ensure the query type is defined and set to 'single' if not provided. It can be 'multiple'.
    if ( typeof this.query_type == 'undefined' ) {
      this.query_type = 'single';
    }
    // Ensure "can be downloaded" is defined and set to true if not provided.
    if ( typeof this.canBeDownloaded == 'undefined' ) {
      this.canBeDownloaded = false;
    }
  }

  /**
   * Updates the filters from the parent component.
   *
   * @param {any} filters - The new filters to update.
   * @return {void}
   */
  updateFiltersFromParentComponent(filters: any): void {
    // Update the filters.
    this.filters = filters;
  }

  /**
   * Retrieves data from the API and populates the dataSource.
   *
   * @return {void} The method does not return a value.
   */
  getData(): void {
    // Normalize the query params.
    const queryParams:{[p: string]: any} = this.utils.normalizeQueryParams({
      ...this.filters,
      query_type: this.query_type,
      date_range: this.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(',')
    });
    // Make a request to get the data from the API.
    this.api.makeRequest('get', 'v2/incidents/charts/stats', {}, queryParams).then((response) => {
      this.dataSource.data = [
        {header: 'Total Incidents', data: response.total_incidents},
        {header: 'LTI Incidents', data: response.lti_incidents},
        {header: 'Completed Incidents', data: response.completed_incidents},
        {header: 'Not Completed Incidents', data: response.not_completed_incidents},
      ];
    });
  }
}

/**
 * Interface representing an incident statistic.
 * @interface
 */
interface IncidentStatistic {
  header: string;
  data: number;
}
